import type { Session, Mix } from "../types/model.types";

export const getCurrentSessionForMix = (mix: Mix): Session => {
  const sessions = mix.sessions;

  if (!mix.sessionsEnabled || sessions.length <= 1) {
    return sessions[0];
  }

  const currentTime = new Date();
  const sessionsToday = getSessionsHappeningToday(sessions);

  // If there are sessions today, prioritize them
  const sessionsToCheck = sessionsToday.length > 0 ? sessionsToday : sessions;

  // First, try to find a currently active session (currentTime is between start and end)
  for (const session of sessionsToCheck) {
    if (session.startTime && session.endTime) {
      const startDate = new Date(session.startTime);
      const endDate = new Date(session.endTime);
      if (currentTime >= startDate && currentTime <= endDate) {
        return session;
      }
    } else if (session.endTime && !session.startTime) {
      const endDate = new Date(session.endTime);
      if (currentTime <= endDate) {
        return session;
      }
    } else if (!session.startTime && !session.endTime) {
      // Session with no time constraints - treat as always active
      return session;
    }
  }

  // If no active session found, find the next upcoming session
  for (const session of sessionsToCheck) {
    if (session.startTime) {
      const startDate = new Date(session.startTime);
      if (startDate > currentTime) {
        return session;
      }
    } else if (!session.endTime) {
      // Session with only startTime (no endTime) that hasn't started yet
      // Since we're checking in order, this is the next one
      return session;
    }
  }

  // If no future sessions exist, return the last session
  return sessions[sessions.length - 1];
};

export const getSessionsHappeningToday = (sessions: Session[]): Session[] => {
  if (!sessions) {
    return [];
  }

  // Get today's date at local midnight
  const today = new Date();
  const todayAtMidnight = new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate(),
  );

  // Get tomorrow at local midnight
  const tomorrowAtMidnight = new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate() + 1,
  );

  const filteredSessions = sessions.filter((session: Session) => {
    const startTime = session.startTime;
    const endTime = session.endTime;

    if (!startTime || !endTime) {
      return false;
    }

    const startDate = new Date(startTime);
    const endDate = new Date(endTime);

    // Check if session time range [startDate, endDate] intersects with today [todayAtMidnight, tomorrowAtMidnight]
    // Two ranges [a, b] and [c, d] intersect if: a < d AND c < b
    return startDate < tomorrowAtMidnight && todayAtMidnight < endDate;
  });

  // Sort sessions by start time
  return filteredSessions.sort((a, b) => {
    if (!a.startTime || !b.startTime) {
      return 0;
    }
    return new Date(a.startTime).getTime() - new Date(b.startTime).getTime();
  });
};
